// app/src/i18n/index.jsx — the app's i18n wiring: web-common's runtime + the 24-locale
// registry, exposed as React context. THE STANDING RULE (D1-DESIGN §8.3, D2a inherits it):
// every user-visible string goes through t() — no literals in JSX.
//
// D-AUTH §3 changes:
//  · The initial locale comes from the COOKIE contract (userLang → lang → browser →
//    en-US via @common/locale-cookie.js) — localStorage('qs.locale') is retired; the
//    cookies ARE the persistence, per the Quocia-FB rule.
//  · THE INSTANT-SWITCH FIX (walkthrough bug 7): the context value used to be the same
//    i18n object identity forever, so a locale change re-rendered only this Provider and
//    React bailed out of the unchanged children element — consumers never saw a new
//    value. The value is now rebuilt per locale change (tag in state), which invalidates
//    every useI18n() consumer at once: switching re-renders NOW, no reload, no
//    click-something-first.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { makeI18n, pickInitialLocale } from '@common/i18n.js';
import { getLocaleFromCookies } from '@common/locale-cookie.js';
import { LOCALES, loadBundle } from './locales.js';

export const LOCALE_TAGS = LOCALES.map((l) => l.tag);

const i18n = makeI18n({
  registry: LOCALES,
  loadBundle,
  initial: pickInitialLocale(LOCALES, getLocaleFromCookies(LOCALE_TAGS)),
});

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [tag, setTag] = useState(i18n.locale());
  const [ready, setReady] = useState(false);
  useEffect(() => {
    i18n.ready.then(() => { setReady(true); setTag(i18n.locale()); });
    return i18n.onChange((t2) => setTag(t2));
  }, []);
  const value = useMemo(
    () => ({ t: i18n.t, locale: i18n.locale, setLocale: i18n.setLocale, onChange: i18n.onChange, registry: i18n.registry, tag }),
    [tag]
  );
  if (!ready) return null; // the en-US chain loads in one tick; a flash of raw keys never ships
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx); // { t, locale, setLocale, registry, tag }
}
